import { EPatientStatus } from 'views/RoomView/RoomView.types'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { RoomViewState } from './roomView.types'

const initialState = {
  rooms: [],
  surgeryStaff: [],
  dischargedPatients: [],
  scheduledSurgeries: [],
  waitingRoomPatients: [],
  patientStatusOptions: [],
  availableSurgeryStaff: [],
  roomViewSettings: {
    showProcedureName: false,
    showStaffAssignment: false,
    showExpectedEndTime: false,
  },
  isLoading: false,
  allowPatientStatusRestrictedActions: false,

  refetchAppConfig: null,
  refetchSurgeryStaff: null,
  refetchRoomViewRooms: null,
  refetchScheduledSurgeries: null,
  refetchPatientStatusOptions: null,
}

export const useRoomViewStore = create<RoomViewState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setAllowPatientStatusRestrictedActions: (allow) => {
        set(
          { allowPatientStatusRestrictedActions: allow },
          false,
          'setAllowPatientStatusRestrictedActions'
        )
      },

      setScheduledSurgeriesAndRooms: (
        scheduledSurgeries,
        rooms,
        patientStatusOptions,
        surgeryStaff,
        roomViewSettings,
        allowPatientStatusRestrictedActions
      ) => {
        set(
          {
            rooms,
            surgeryStaff,
            scheduledSurgeries,
            patientStatusOptions,
            roomViewSettings,
            waitingRoomPatients: scheduledSurgeries
              .filter(
                (surgery) =>
                  surgery.patientStatusHistory[0]?.status ===
                  EPatientStatus.CHECKED_IN
              )
              .map((surgery) => surgery.patient),
            dischargedPatients: scheduledSurgeries
              .filter(
                (surgery) =>
                  surgery.patientStatusHistory[0]?.status ===
                  EPatientStatus.CHECKED_OUT
              )
              .map((surgery) => surgery.patient),
            availableSurgeryStaff: surgeryStaff.map((staff) => {
              const isAssignedToActiveProcedure = scheduledSurgeries.some(
                (surgery) => {
                  const isInRoom =
                    surgery.patientStatusHistory[0]?.status !==
                    EPatientStatus.ASSIGN_ROOM
                  const isAssignedToThisSurgery = surgery.surgeryStaff.some(
                    (assignedStaff) => assignedStaff._id === staff._id
                  )
                  return !isInRoom && isAssignedToThisSurgery
                }
              )
              return {
                ...staff,
                isAvailable: !isAssignedToActiveProcedure,
              }
            }),
            allowPatientStatusRestrictedActions,
          },
          false,
          'setScheduledSurgeriesAndRooms'
        )
      },

      setRooms: (rooms) => {
        set({ rooms }, false, 'setRooms')
      },

      setPatientStatusOptions: (patientStatusOptions) => {
        set({ patientStatusOptions }, false, 'setPatientStatusOptions')
      },

      setRoomViewSettings: (settings) => {
        set({ roomViewSettings: settings }, false, 'setRoomViewSettings')
      },

      setRefetchScheduledSurgeries: (refetch) => {
        set(
          { refetchScheduledSurgeries: refetch },
          false,
          'setRefetchScheduledSurgeries'
        )
      },

      setRefetchRoomViewRooms: (refetch) => {
        set({ refetchRoomViewRooms: refetch }, false, 'setRefetchRoomViewRooms')
      },

      setRefetchPatientStatusOptions: (refetch) => {
        set(
          { refetchPatientStatusOptions: refetch },
          false,
          'setRefetchPatientStatusOptions'
        )
      },

      setRefetchSurgeryStaff: (refetch) => {
        set({ refetchSurgeryStaff: refetch }, false, 'setRefetchSurgeryStaff')
      },

      setRefetchAppConfig: (refetch) => {
        set({ refetchAppConfig: refetch }, false, 'setRefetchAppConfig')
      },

      setIsLoading: (loading) => {
        set({ isLoading: loading }, false, 'setIsLoading')
      },

      refetchAll: async () => {
        const {
          refetchScheduledSurgeries,
          refetchRoomViewRooms,
          refetchPatientStatusOptions,
          refetchSurgeryStaff,
          refetchAppConfig,
          setIsLoading,
          setAllowPatientStatusRestrictedActions,
        } = get()

        if (
          refetchScheduledSurgeries &&
          refetchRoomViewRooms &&
          refetchPatientStatusOptions &&
          refetchSurgeryStaff &&
          refetchAppConfig
        ) {
          try {
            setIsLoading(true)
            const [
              surgeriesResult,
              roomsResult,
              patientStatusResult,
              surgeryStaffResult,
              appConfigResult,
            ] = await Promise.all([
              refetchScheduledSurgeries(),
              refetchRoomViewRooms(),
              refetchPatientStatusOptions(),
              refetchSurgeryStaff(),
              refetchAppConfig(),
            ])

            const surgeriesData =
              surgeriesResult?.data?.getRoomViewScheduledSurgeries
            const roomsData = roomsResult?.data?.getRoomViewRooms
            const patientStatusOptions = patientStatusResult?.data?.data
            const surgeryStaff = surgeryStaffResult?.data?.getSurgeryStaff
            const roomViewSettings =
              appConfigResult?.data?.getAppConfig?.roomViewSettings
            const allowPatientStatusRestrictedActions =
              appConfigResult?.data?.getAppConfig?.featureFlags
                ?.allowPatientStatusRestrictedActions

            if (
              appConfigResult?.data?.getAppConfig?.featureFlags?.hasOwnProperty(
                'allowPatientStatusRestrictedActions'
              )
            ) {
              setAllowPatientStatusRestrictedActions(
                appConfigResult.data.getAppConfig.featureFlags
                  .allowPatientStatusRestrictedActions
              )
            }

            if (
              surgeriesData &&
              roomsData &&
              patientStatusOptions &&
              surgeryStaff &&
              roomViewSettings
            ) {
              get().setScheduledSurgeriesAndRooms(
                surgeriesData,
                roomsData,
                patientStatusOptions,
                surgeryStaff,
                roomViewSettings,
                allowPatientStatusRestrictedActions
              )
            }
          } catch (error) {
            console.error('Error refetching data:', error)
          } finally {
            setIsLoading(false)
          }
        }
      },
    }),
    {
      name: 'room-view-store',
    }
  )
)

export const useAllowPatientStatusRestrictedActions = () =>
  useRoomViewStore((state) => state.allowPatientStatusRestrictedActions)

export const useSetAllowPatientStatusRestrictedActions = () =>
  useRoomViewStore((state) => state.setAllowPatientStatusRestrictedActions)

export const useScheduledSurgeries = () =>
  useRoomViewStore((state) => state.scheduledSurgeries)

export const useWaitingRoomPatients = () =>
  useRoomViewStore((state) => state.waitingRoomPatients)

export const useRooms = () => useRoomViewStore((state) => state.rooms)

export const useRefetchScheduledSurgeries = () =>
  useRoomViewStore((state) => state.refetchScheduledSurgeries)

export const useRefetchRoomViewRooms = () =>
  useRoomViewStore((state) => state.refetchRoomViewRooms)

export const useRefetchAll = () => useRoomViewStore((state) => state.refetchAll)

export const useAvailableSurgeryStaff = () =>
  useRoomViewStore((state) => state.availableSurgeryStaff)

export const useRoomViewLoading = () =>
  useRoomViewStore((state) => state.isLoading)

export const useSetRoomViewLoading = () =>
  useRoomViewStore((state) => state.setIsLoading)

export const useRoomViewSettings = () =>
  useRoomViewStore((state) => state.roomViewSettings)

export const useRefetchAppConfig = () =>
  useRoomViewStore((state) => state.refetchAppConfig)
